// IOSInstallHint — small dismissible banner shown ONLY on iPhone Safari (not
// in standalone mode, not in Capacitor WebView, not on Android, not on desktop).
// iOS Safari doesn't support the beforeinstallprompt event, so the standard
// pattern is a user-facing nudge explaining the Share → Add to Home Screen flow.
//
// Dismissal is remembered in localStorage so the user only sees it once.
import { useEffect, useState } from "react";
import { X, Share, Plus } from "lucide-react";

const DISMISS_KEY = "supermorsel.ios.install.dismissed.v1";

function isIPhone() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  // iPhone-only (we don't want to nag iPad — they have a different UX)
  return /iPhone|iPod/.test(ua);
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  // iOS exposes navigator.standalone; other browsers use display-mode: standalone
  return (window.navigator).standalone === true ||
    window.matchMedia?.("(display-mode: standalone)").matches;
}

function isCapacitor() {
  return typeof window !== "undefined" && !!(window.Capacitor || window.NativeBridge);
}

export default function IOSInstallHint() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!isIPhone()) return;
    if (isStandalone()) return; // already installed
    if (isCapacitor()) return;  // already in the native wrapper
    try {
      if (localStorage.getItem(DISMISS_KEY)) return;
    } catch { /* localStorage may be blocked in private mode — show anyway */ }
    // Defer a beat so it doesn't shove during initial paint / auth bootstrap.
    const t = setTimeout(() => setShow(true), 1500);
    return () => clearTimeout(t);
  }, []);

  if (!show) return null;

  const dismiss = () => {
    setShow(false);
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* ignore */ }
  };

  return (
    <div
      className="fixed left-3 right-3 bottom-3 z-[60] rounded-2xl shadow-2xl border border-emerald-200 bg-white animate-in slide-in-from-bottom-4 duration-300"
      style={{ paddingBottom: "max(env(safe-area-inset-bottom), 0px)" }}
      role="dialog"
      data-testid="ios-install-hint"
    >
      <div className="p-3 flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-emerald-600 text-white flex items-center justify-center flex-shrink-0">
          <Plus size={18} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-extrabold text-gray-900">Add Super Morsel to your Home Screen</p>
          <p className="text-[11px] text-gray-600 leading-snug mt-0.5">
            Tap <Share size={11} className="inline -mt-0.5 text-emerald-600" /> Share, then
            <span className="font-semibold text-gray-800"> &ldquo;Add to Home Screen&rdquo;</span> for the full app experience — no Safari chrome.
          </p>
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          className="w-7 h-7 rounded-full hover:bg-gray-100 flex items-center justify-center flex-shrink-0 text-gray-500"
          data-testid="ios-install-dismiss"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
